(ns arthur.ui.shell
  "The window: one grid, five panes, a location bar, and the clock's audio
  element on the backend that needs one.

  Nothing else. Each pane owns its own subscriptions, so this component re-renders
  only when the grid itself would change — which is a pane being resized, shut or
  reopened, and nothing a pane's contents can do. The picture is put on
  the canvas by `ui/player`'s loop rather than by anything here re-rendering,
  which is why scrubbing at speed does not touch React at all."
  (:require [arthur.clock :as clock]
            [arthur.ui.convert :as convert]
            [arthur.events.playback :as pb]
            [arthur.subs.playback :as playback]
            [arthur.subs.render :as render]
            [arthur.subs.ui :as sub]
            [arthur.ui.layout :as layout]
            [arthur.ui.location :as location]
                        [arthur.ui.params :as params]
            [arthur.ui.pool :as pool]
            [arthur.ui.shortcuts :as shortcuts]
            [arthur.ui.stage :as stage]
            [arthur.ui.tabs :as tabs]
            [arthur.ui.timeline :as timeline]
            [arthur.ui.tools :as tools]
            [arthur.ui.topbar :as topbar]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(defn- audio []
  ;; An edit to what the open symbol plays — a sound dropped, moved, turned
  ;; down, undone — re-mixes the clock. Opening a symbol mixes on its own, so
  ;; only a change under the same one counts.
  (r/with-let [heard (atom nil)
               remix (r/track! (fn []
                             (let [[id sid :as now] @(rf/subscribe [::render/sounds])
                                   [was-id was-sid :as before] @heard]
                               (reset! heard now)
                               ;; The same symbol OF THE SAME DOCUMENT. A
                               ;; different document, or a different symbol, has
                               ;; already asked for its own clock — see
                               ;; `::pb/clock!`'s callers — and re-mixing here as
                               ;; well is a second render of minutes of audio.
                               (when (and before
                                          (= [id sid] [was-id was-sid])
                                          (not= now before))
                                 (rf/dispatch [::pb/refresh-clock])))))]
    ;; NOTHING TO RENDER under the graph backend: it plays the mixdown buffer
    ;; directly and takes its position from the audio context, so there is no
    ;; element and no WAV for one to hold. The `remix` track above still runs
    ;; either way — it is what notices an edit to a track, and `with-let` builds
    ;; it whatever this body returns.
    (when-not (clock/graph?)
      [:audio
       {:ref     #(when % (clock/attach! %))
        :src     @(rf/subscribe [::playback/audio])
        :preload "auto"
        ;; Transport state follows the ELEMENT, not the other way round: the audio is
        ;; the clock, so anything that can change its state — the end of the file, the
        ;; OS media keys, a browser autoplay block — has to be able to correct the
        ;; document rather than be contradicted by it.
        :on-play  #(rf/dispatch [::pb/play])
        :on-pause #(rf/dispatch [::pb/pause])}])
    (finally (r/dispose! remix))))

(defn view []
  ;; The only component that re-renders when the grid changes, which is what the
  ;; namespace docstring says never happens — it happens now, on a pane being
  ;; dragged or shut, and that is the whole of it: every pane still owns its own
  ;; subscriptions and a drag of the inspector's edge re-renders this div and no
  ;; pane's contents.
  (let [{:keys [pool params shut] :as state} @(rf/subscribe [::layout/state])
        gone? #(contains? shut %)]
    ;; `read-only`: somebody else's project. The edits themselves are refused
    ;; in `events/edit`; this greys out the controls that would make them.
    [:div.app {:style (layout/vars state)
               :class (when @(rf/subscribe [::sub/read-only?]) "read-only")}
     [topbar/view]
     [shortcuts/sheet]
     (when-not (gone? :pool) [pool/view])
     (when-not (gone? :pool) [layout/grip :pool pool :col 1])
     [:section.view
      [tabs/view]
      [tools/options]
      [:div.workspace
       [tools/toolbox]
       [stage/view]]]
     (when-not (gone? :params) [layout/grip :params params :col -1])
     (when-not (gone? :params) [params/view])
     ;; Above the timeline rather than inside it: the bar says where an edit would
     ;; land, which is a fact about the SELECTION and not about either temporal
     ;; view, and both views are drawn below it unchanged.
     [location/view]
     (when-not (gone? :time) [layout/grip :time (:time state) :row -1])
     [timeline/view]
     [convert/view]
     [audio]]))
